import { Component } from '@theme/component';

/** Earth's axial tilt, in radians. */
const AXIAL_TILT = (23.4 * Math.PI) / 180;
/** Tips the north pole slightly toward the viewer so more land shows. */
const PITCH = (12 * Math.PI) / 180;
/** Seconds for one full turn of the Earth. */
const TURN_SECONDS = 90;
/** How far the camera swings around the Earth by the end of the scroll, in radians. */
const CAMERA_SWING = (40 * Math.PI) / 180;
/** Scroll progress where the Earth has fully appeared; the camera then swings on its own. */
const PLANET_SHOWN = 0.6;
/** Scroll progress where everything has been revealed and the section locks in its final state. */
const REVEALED = 0.97;
/** How long the camera's swing takes, in milliseconds. */
const SWING_MS = 14000;
/** How quickly the story catches up with the scroll position: lower is snappier. */
const GLIDE_MS = 140;
/** On-screen viewing time after the camera starts moving before the flying saucer passes by. */
const UFO_DELAY_MS = 20000;
/** How long the saucer takes to cross the screen. */
const UFO_FLIGHT_MS = 10000;
/**
 * Where the sun and moon sit in the sky, as seen from the Earth: angle around (negative is to the
 * left of where the camera starts) and angle up, in radians. Both start out of view on the left.
 */
const SUN = { around: (-86 * Math.PI) / 180, up: (4 * Math.PI) / 180 };
const MOON = { around: (-70 * Math.PI) / 180, up: (19 * Math.PI) / 180 };
/** Sunlight on the Earth before and after the swing: from the front left, then low from the left. */
const LIGHT_START = [-0.55, 0.4, 0.75];
const LIGHT_END = [-0.9, 0.2, 0.42];
/** Largest planet canvas, in device pixels. */
const MAX_PLANET_PIXELS = 720;
/** Frame interval for the sky and planet (about 30 frames a second). */
const FRAME_MS = 33;

/** Dark patches (lunar seas) as offsets and sizes relative to the moon's radius. */
const MOON_SEAS = [
  [-0.28, -0.3, 0.32],
  [0.12, -0.38, 0.22],
  [0.28, -0.05, 0.26],
  [-0.05, 0.05, 0.2],
  [-0.35, 0.22, 0.18],
  [0.18, 0.38, 0.14],
];
/** Small craters: offset x, offset y, size. */
const MOON_CRATERS = [
  [0.1, 0.62, 0.09],
  [-0.55, -0.05, 0.07],
  [0.5, 0.35, 0.06],
  [-0.2, 0.55, 0.05],
  [0.42, -0.5, 0.05],
];

/** @type {Map<string, Promise<{ pixels: Uint32Array, width: number, height: number }>>} */
const textures = new Map();

/**
 * Loads a flat world map once and keeps its pixels for every planet that uses it.
 * @param {string} url
 */
function loadTexture(url) {
  let texture = textures.get(url);
  if (!texture) {
    texture = (async () => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('No 2D context');
      context.drawImage(image, 0, 0);
      const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
      return { pixels: new Uint32Array(data.buffer), width: canvas.width, height: canvas.height };
    })();
    textures.set(url, texture);
  }
  return texture;
}

/** @param {number[]} vector */
function normalize(vector) {
  const length = Math.hypot(...vector) || 1;
  return vector.map((value) => value / length);
}

/** @param {number} value */
function smooth(value) {
  const t = Math.min(Math.max(value, 0), 1);
  return t * t * (3 - 2 * t);
}

/**
 * @typedef {object} SphereLook
 * @property {number} tilt - Axis tilt in the screen plane, in radians.
 * @property {number} pitch - How far the north pole tips toward the viewer, in radians.
 * @property {number} ambient - Light on the night side.
 * @property {number} gain - Strength of direct sunlight.
 * @property {number} curve - How quickly light falls off toward the night side.
 * @property {boolean} haze - Blue atmosphere toward the edge.
 * @property {boolean} glint - Sun glint on water.
 */

/** @type {SphereLook} */
const EARTH_LOOK = { tilt: AXIAL_TILT, pitch: PITCH, ambient: 0.16, gain: 1.04, curve: 0.8, haze: true, glint: true };
/** @type {SphereLook} */
const MOON_LOOK = { tilt: 0, pitch: 0, ambient: 0.03, gain: 1.2, curve: 1, haze: false, glint: false };

/**
 * Draws a lit sphere from a flat map. Where each pixel lands on the map is worked out once per
 * size; the lighting is reworked only when the sun direction changes.
 */
class PlanetRenderer {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ pixels: Uint32Array, width: number, height: number }} texture
   * @param {SphereLook} [look]
   */
  constructor(canvas, texture, look = EARTH_LOOK) {
    this.canvas = canvas;
    this.texture = texture;
    this.look = look;
    this.context = canvas.getContext('2d');
    this.size = 0;
    /** @type {number[]} */
    this.lightDirection = [];
  }

  /** Matches the canvas resolution to its displayed size. */
  resize() {
    // Layout size, so the zoom-in scale doesn't lower the resolution
    this.build(
      Math.min(Math.round(this.canvas.offsetWidth * Math.min(window.devicePixelRatio || 1, 2)), MAX_PLANET_PIXELS)
    );
  }

  /**
   * Sets the canvas to the given size in pixels and rebuilds the lookup tables.
   * @param {number} size
   */
  build(size) {
    if (size < 2 || size === this.size) return;

    this.size = size;
    this.canvas.width = size;
    this.canvas.height = size;
    this.image = this.context?.createImageData(size, size);
    this.output = this.image ? new Uint32Array(this.image.data.buffer) : null;

    const { width, height } = this.texture;
    const count = size * size;
    this.row = new Int32Array(count).fill(-1);
    this.column = new Float32Array(count);
    this.normalX = new Float32Array(count);
    this.normalY = new Float32Array(count);
    this.normalZ = new Float32Array(count);
    this.light = new Float32Array(count);
    this.shine = new Float32Array(count);
    this.rim = new Float32Array(count);
    this.alpha = new Uint8Array(count);

    const radius = size / 2;
    const tiltCos = Math.cos(this.look.tilt);
    const tiltSin = Math.sin(this.look.tilt);
    const pitchCos = Math.cos(this.look.pitch);
    const pitchSin = Math.sin(this.look.pitch);

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const nx = (x + 0.5 - radius) / radius;
        const ny = -(y + 0.5 - radius) / radius;
        const distance = nx * nx + ny * ny;
        if (distance > 1) continue;

        const i = y * size + x;
        const nz = Math.sqrt(1 - distance);
        this.normalX[i] = nx;
        this.normalY[i] = ny;
        this.normalZ[i] = nz;

        // Smooth the outline over about one pixel.
        const edge = (1 - Math.sqrt(distance)) * radius;
        this.alpha[i] = Math.round(Math.min(Math.max(edge, 0), 1) * 255);

        // Undo the axial tilt (in the screen plane), then the pitch toward the viewer.
        const tx = nx * tiltCos - ny * tiltSin;
        const ty = nx * tiltSin + ny * tiltCos;
        const py = ty * pitchCos + nz * pitchSin;
        const pz = nz * pitchCos - ty * pitchSin;

        const latitude = Math.asin(Math.min(Math.max(py, -1), 1));
        const longitude = Math.atan2(tx, pz);
        const row = Math.min(height - 1, Math.max(0, Math.floor((0.5 - latitude / Math.PI) * height)));
        this.row[i] = row * width;
        this.column[i] = (longitude / (2 * Math.PI) + 0.5) * width;
        this.rim[i] = this.look.haze ? Math.pow(1 - nz, 3) : 0;
      }
    }

    const light = this.lightDirection.length ? this.lightDirection : LIGHT_START;
    this.lightDirection = [];
    this.setLight(light);
  }

  /** @param {number[]} direction - Direction toward the sun, in screen space (x right, y up, z toward viewer). */
  setLight(direction) {
    const sun = normalize(direction);
    const previous = this.lightDirection;
    if (
      previous.length &&
      Math.abs((previous[0] ?? 0) - (sun[0] ?? 0)) + Math.abs((previous[1] ?? 0) - (sun[1] ?? 0)) < 0.002
    ) {
      return;
    }
    this.lightDirection = sun;
    if (!this.row || !this.normalX || !this.normalY || !this.normalZ || !this.light || !this.shine) return;

    const [sx = 0, sy = 0, sz = 1] = sun;
    const [hx = 0, hy = 0, hz = 1] = normalize([sx, sy, sz + 1]);
    const { row, normalX, normalY, normalZ, light, shine } = this;
    const { ambient, gain, curve, glint } = this.look;
    for (let i = 0; i < row.length; i++) {
      if ((row[i] ?? -1) < 0) continue;
      const nx = normalX[i] ?? 0;
      const ny = normalY[i] ?? 0;
      const nz = normalZ[i] ?? 0;
      const diffuse = Math.max(0, nx * sx + ny * sy + nz * sz);
      light[i] = ambient + gain * Math.pow(diffuse, curve);
      shine[i] = glint ? Math.pow(Math.max(0, nx * hx + ny * hy + nz * hz), 40) * 45 : 0;
    }
  }

  /** @param {number} turn - Fraction of a full turn, 0 → 1. */
  draw(turn) {
    if (!this.output || !this.image || !this.context || !this.row) return;
    const { pixels, width } = this.texture;
    const { row, column, light, shine, rim, alpha, output } = this;
    if (!column || !light || !shine || !rim || !alpha) return;
    // Earth turns west to east, so the surface moves left to right on screen.
    const offset = width - (((turn % 1) + 1) % 1) * width;

    for (let i = 0; i < output.length; i++) {
      const base = row[i] ?? -1;
      if (base < 0) {
        output[i] = 0;
        continue;
      }

      const texel = pixels[base + ((((column[i] ?? 0) + offset) | 0) % width)] ?? 0;
      let r = texel & 255;
      let g = (texel >> 8) & 255;
      let b = (texel >> 16) & 255;

      // Water catches a soft sun glint, fading smoothly with how blue the pixel is.
      const blueness = (b - (r > g ? r : g)) / 48;
      const glint = blueness > 0 ? (shine[i] ?? 0) * (blueness < 1 ? blueness : 1) : 0;
      const lit = light[i] ?? 1;
      const haze = rim[i] ?? 0;
      r = r * lit + glint + haze * 70;
      g = g * lit + glint + haze * 130;
      b = b * lit + glint + haze * 255;

      output[i] =
        (((alpha[i] ?? 255) << 24) |
          ((b > 255 ? 255 : b) << 16) |
          ((g > 255 ? 255 : g) << 8) |
          (r > 255 ? 255 : r)) >>>
        0;
    }

    this.context.putImageData(this.image, 0, 0);
  }
}

/**
 * Pins the name-meaning section and drives its scroll story. It writes the scroll progress
 * (0 → 1) to `--p`; CSS turns that into the space, split, planet, meaning and closing stages.
 * Each word part gets `--part-offset` so the parts spread out evenly from the center.
 *
 * It also paints the sky and the Earth. Once the Earth has appeared, the camera swings slowly
 * around it on its own while it stays centered and keeps turning: the stars slide past, a distant
 * moon and then the sun with its rays come into view on the left, and the sunlight on the Earth
 * follows the sun. When the visitor reaches the end, the section locks in its final state and stops
 * pinning, so scrolling back doesn't rewind it.
 * With reduced motion the section isn't pinned and the final view holds still.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [parts] - The word parts, in order.
 * @property {HTMLElement} [panel] - The panel that holds everything.
 * @property {HTMLCanvasElement} [sky] - Canvas for the stars, sun and moon.
 * @property {HTMLCanvasElement} [planet] - Canvas for the planet.
 *
 * @extends {Component<Refs>}
 */
class NameMeaning extends Component {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /** @type {IntersectionObserver | null} */
  #visibility = null;

  /** @type {ResizeObserver | null} */
  #resizer = null;

  /** @type {PlanetRenderer | null} */
  #planet = null;

  /** @type {PlanetRenderer | null} */
  #moon = null;

  /** @type {HTMLImageElement | null} */
  #sunImage = null;

  /** @type {{ x: number, y: number, size: number, glow: number, speed: number, phase: number, tint: string }[]} */
  #stars = [];

  #ratio = 1;
  #focal = 1;
  #progress = 1;
  /** Scroll position the shown progress is gliding toward. */
  #target = 1;
  #lastGlide = 0;
  /** When the camera started swinging (performance.now), or null before it starts. */
  /** @type {number | null} */
  #swingStart = null;
  #revealed = false;
  /** Time spent on screen since the camera started moving, in milliseconds. */
  #watched = 0;
  #lastWatch = 0;
  /** When the flying saucer set off (performance.now), or null before it does. */
  /** @type {number | null} */
  #ufoStart = null;
  #frame = 0;
  #loop = 0;
  #lastDraw = 0;
  #visible = false;
  #setupRun = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#setup);
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
    this.#motionQuery?.removeEventListener('change', this.#setup);
  }

  get #still() {
    return Boolean(this.#motionQuery?.matches);
  }

  #setup = () => {
    this.#teardown();
    const run = ++this.#setupRun;

    const parts = this.refs.parts ?? [];
    const center = (parts.length - 1) / 2;
    parts.forEach((part, index) => part.style.setProperty('--part-offset', String(index - center)));
    // Measure again once the display font arrives, since it changes the parts' widths
    document.fonts?.ready.then(() => {
      if (run === this.#setupRun) this.#placeLabels();
    });

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    if (this.#still) {
      delete this.dataset.enhanced;
      delete this.dataset.revealed;
      this.style.removeProperty('--p');
      this.#progress = 1;
      this.#swingStart = -SWING_MS;
    } else if (this.#revealed) {
      // Already seen: keep the finished view without pinning.
      this.dataset.enhanced = '';
      this.dataset.revealed = '';
      this.style.removeProperty('--p');
      this.#progress = 1;
      this.#swingStart ??= performance.now();
    } else {
      this.dataset.enhanced = '';
      // Horizon scrolls `.page-wrapper` on wide screens and the document on narrow ones; a
      // capturing listener on the document hears both.
      document.addEventListener('scroll', this.#schedule, { passive: true, capture: true, signal });
      window.addEventListener('resize', this.#schedule, { passive: true, signal });
      this.#update(true);
    }

    this.#resizer = new ResizeObserver(() => this.#resize());
    if (this.refs.panel) this.#resizer.observe(this.refs.panel);

    this.#visibility = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible) this.#start();
      else cancelAnimationFrame(this.#loop);
    });
    this.#visibility.observe(this);

    this.#loadPlanet(run);
    this.#loadMoonAndSun(run);
  };

  #teardown() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#visibility?.disconnect();
    this.#visibility = null;
    this.#resizer?.disconnect();
    this.#resizer = null;
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#loop);
  }

  /** @param {number} run - Ignores the result if setup ran again meanwhile. */
  async #loadPlanet(run) {
    const canvas = this.refs.planet;
    const url = this.dataset.texture;
    if (!canvas || !url) return;

    try {
      const texture = await loadTexture(url);
      if (run !== this.#setupRun || !this.isConnected) return;
      this.#planet = new PlanetRenderer(canvas, texture);
      this.#planet.resize();
      this.dataset.planetReady = '';
      this.#drawFrame(performance.now());
      this.#start();
    } catch {
      // Without the map the planet stays hidden and the rest of the section still works.
      delete this.dataset.planetReady;
    }
  }

  /** @param {number} run - Ignores the result if setup ran again meanwhile. */
  async #loadMoonAndSun(run) {
    const { moon, sun } = this.dataset;
    if (moon && !this.#moon) {
      try {
        const texture = await loadTexture(moon);
        if (run === this.#setupRun) {
          this.#moon = new PlanetRenderer(document.createElement('canvas'), texture, MOON_LOOK);
        }
      } catch {
        // The moon falls back to a simple painted one.
      }
    }
    if (sun && !this.#sunImage) {
      try {
        const image = new Image();
        image.src = sun;
        await image.decode();
        if (run === this.#setupRun) this.#sunImage = image;
      } catch {
        // The sun falls back to a plain glowing core.
      }
    }
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Reads where the scroll is. The shown progress then glides toward it each frame (see #tick),
   * so wheel steps don't make the story jump.
   * @param {boolean} [jump] - Show the new position straight away (first load, resizes).
   */
  #update = (jump = false) => {
    if (this.#revealed) return;
    const rect = this.getBoundingClientRect();
    const travel = Math.max(rect.height - window.innerHeight, 1);
    this.#target = Math.min(Math.max(-rect.top / travel, 0), 1);
    if (jump === true) {
      this.#progress = this.#target;
      this.#applyProgress();
    }
  };

  #applyProgress() {
    this.style.setProperty('--p', this.#progress.toFixed(4));
    if (this.#progress >= PLANET_SHOWN) this.#swingStart ??= performance.now();
    if (this.#progress >= REVEALED) this.#lockRevealed(this.getBoundingClientRect());
  }

  /**
   * Moves the shown progress part of the way toward the scroll position.
   * @param {number} now
   */
  #glide(now) {
    const elapsed = Math.min(now - this.#lastGlide, 100);
    this.#lastGlide = now;
    if (this.#revealed || this.#still) return;
    const gap = this.#target - this.#progress;
    if (Math.abs(gap) < 0.0004) return;
    this.#progress += gap * (1 - Math.exp(-elapsed / GLIDE_MS));
    this.#applyProgress();
  }

  /**
   * Keeps the finished view for good: the section stops pinning and drops to a single screen, so
   * scrolling back up doesn't rewind it. The page is shifted by the height that went away, so
   * whatever the visitor is looking at stays put.
   * @param {DOMRect} rect - The section's box just before it shrinks.
   */
  #lockRevealed(rect) {
    this.#revealed = true;
    this.#listeners?.abort();
    this.#listeners = null;
    this.style.removeProperty('--p');
    this.#progress = 1;
    this.dataset.revealed = '';

    const after = this.getBoundingClientRect();
    // Inside the pinned stretch the panel was at the top of the screen: bring the section's top there.
    // Past the section: everything below moved up by the height that went away.
    const delta = rect.bottom > 0 ? rect.top : after.height - rect.height;
    if (Math.abs(delta) < 1) return;
    const scroller = this.closest('.page-wrapper');
    if (scroller instanceof HTMLElement && scroller.scrollHeight > scroller.clientHeight) {
      scroller.scrollBy({ top: delta, behavior: 'instant' });
    } else {
      window.scrollBy({ top: delta, behavior: 'instant' });
    }
  }

  /** How far the camera has swung, 0 → 1. It plays on its own once the Earth has appeared. */
  get #swing() {
    if (this.#swingStart === null) return 0;
    return smooth((performance.now() - this.#swingStart) / SWING_MS);
  }

  #resize() {
    this.#planet?.resize();
    this.#makeStars();
    this.#placeLabels();
    this.#drawFrame(performance.now());
  }

  /**
   * Works out how far each part's label must slide so that, once the word rejoins, the labels sit
   * side by side as one group centered under the whole word (and the Earth), whatever the parts'
   * widths. CSS applies the slide as the word rejoins (--merge).
   */
  #placeLabels = () => {
    const parts = this.refs.parts ?? [];
    const word = parts[0]?.parentElement;
    if (!word || !parts.length) return;

    const labels = parts.map((part) => part.querySelector('.name-part__details'));
    const widths = labels.map((label) => (label instanceof HTMLElement ? label.offsetWidth : 0));
    // Space between the labels: 5% of the screen width, between 20 and 48 pixels
    const gapPixels = Math.min(Math.max(window.innerWidth * 0.05, 20), 48);
    const total = widths.reduce((sum, width) => sum + width, 0) + gapPixels * (parts.length - 1);
    let start = word.offsetWidth / 2 - total / 2;

    parts.forEach((part, index) => {
      const width = widths[index] ?? 0;
      const target = start + width / 2;
      start += width + gapPixels;
      // offsetLeft ignores transforms, so this is where the part sits once rejoined
      const natural = part.offsetLeft + part.offsetWidth / 2;
      part.style.setProperty('--label-shift', `${(target - natural).toFixed(1)}px`);
    });
  };

  #makeStars() {
    const canvas = this.refs.sky;
    if (!canvas) return;
    this.#ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    canvas.width = Math.round(width * this.#ratio);
    canvas.height = Math.round(height * this.#ratio);

    // Lens strength: wide screens see more sky to the sides; phones keep the sun and moon in reach.
    this.#focal = (Math.min(width, height * 1.2) / 2 / Math.tan((35 * Math.PI) / 180)) * this.#ratio;

    // Stars cover the screen plus the distance the camera swings, so none run out mid-swing.
    const reach = this.#focal * CAMERA_SWING * 1.05;
    const span = canvas.width + reach;
    const count = Math.min(Math.round((span * canvas.height) / (2600 * this.#ratio * this.#ratio)), 700);
    const tints = ['255 255 255', '255 255 255', '255 255 255', '200 220 255', '255 236 200'];
    this.#stars = Array.from({ length: count }, () => {
      const bright = Math.random() < 0.08;
      return {
        x: Math.random() * span - reach,
        y: Math.random() * canvas.height,
        size: (bright ? 1.1 + Math.random() * 0.8 : 0.35 + Math.random() * 0.7) * this.#ratio,
        glow: bright ? 0.9 : 0.25 + Math.random() * 0.55,
        speed: 0.4 + Math.random() * 1.6,
        phase: Math.random() * Math.PI * 2,
        tint: tints[Math.floor(Math.random() * tints.length)] ?? '255 255 255',
      };
    });
  }

  /** Where the Earth's center sits on the sky canvas, in canvas pixels, and its radius. */
  #earthCenter() {
    const canvas = this.refs.sky;
    const planet = this.refs.planet?.parentElement;
    const stage = planet?.parentElement;
    if (!canvas || !planet || !stage) return { x: 0, y: 0, radius: 0 };
    const sky = canvas.getBoundingClientRect();
    const box = stage.getBoundingClientRect();
    return {
      x: (box.left + box.width / 2 - sky.left) * this.#ratio,
      y: (box.top + box.height / 2 - sky.top) * this.#ratio,
      radius: (planet.offsetWidth / 2) * this.#ratio,
    };
  }

  /**
   * Screen position of something far away, given the camera swing.
   * @param {{ around: number, up: number }} place
   * @param {{ x: number, y: number }} earth
   * @param {number} swing - Radians.
   */
  #project(place, earth, swing) {
    const around = place.around + swing;
    const facing = Math.cos(around);
    return {
      x: earth.x + this.#focal * Math.tan(around),
      y: earth.y - (this.#focal * Math.tan(place.up)) / Math.max(facing, 0.05),
      facing,
    };
  }

  /** @param {number} now */
  #drawSky(now) {
    const canvas = this.refs.sky;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);

    const swing = this.#swing * CAMERA_SWING;
    const shift = this.#focal * swing;
    const seconds = now / 1000;

    for (const star of this.#stars) {
      const x = star.x + shift;
      if (x < -4 || x > canvas.width + 4) continue;
      const twinkle = this.#still ? 1 : 0.65 + 0.35 * Math.sin(seconds * star.speed + star.phase);
      context.fillStyle = `rgb(${star.tint} / ${(star.glow * twinkle).toFixed(3)})`;
      context.beginPath();
      context.arc(x, star.y, star.size, 0, Math.PI * 2);
      context.fill();
    }

    const earth = this.#earthCenter();
    if (!earth.radius) return;
    const sun = this.#project(SUN, earth, swing);
    const moon = this.#project(MOON, earth, swing);
    // The far objects fade in as they come around, so they never pop into view.
    if (moon.facing > 0.2) {
      this.#drawMoon(context, moon, earth.radius * 0.14, sun, Math.min((moon.facing - 0.2) / 0.3, 1));
    }
    if (sun.facing > 0.05) {
      this.#drawSun(context, sun, earth.radius * 0.1, Math.min((sun.facing - 0.05) / 0.35, 1), seconds);
    }

    // The sky sits under the planet, so the saucer disappears behind the Earth as it crosses.
    if (this.#ufoStart !== null) {
      const flight = (now - this.#ufoStart) / UFO_FLIGHT_MS;
      if (flight >= 0 && flight <= 1) this.#drawUfo(context, canvas.width, earth, flight, seconds);
    }
  }

  /**
   * Adds up time on screen once the camera has started moving, and sends the saucer off once
   * enough has passed. It flies once per visit.
   * @param {number} now
   */
  #countWatching(now) {
    const elapsed = Math.min(now - this.#lastWatch, 100);
    this.#lastWatch = now;
    if (this.#still || this.#swingStart === null || this.#ufoStart !== null) return;
    this.#watched += elapsed;
    if (this.#watched >= UFO_DELAY_MS) this.#ufoStart = now;
  }

  /**
   * A small silver flying saucer with a glowing dome and blinking rim lights. It crosses from left
   * to right with a gentle wobble, shrinking a little toward the middle as it heads away.
   * @param {CanvasRenderingContext2D} context
   * @param {number} width - Sky canvas width.
   * @param {{ x: number, y: number, radius: number }} earth
   * @param {number} flight - 0 → 1 across the screen.
   * @param {number} seconds
   */
  #drawUfo(context, width, earth, flight, seconds) {
    const base = earth.radius * 0.09;
    const size = base * (1 - 0.35 * Math.sin(Math.PI * flight));
    const x = -base * 3 + (width + base * 6) * flight;
    const bob = Math.sin(flight * Math.PI * 6);
    const y = earth.y - earth.radius * 0.18 + bob * earth.radius * 0.035;
    const tilt = Math.cos(flight * Math.PI * 6) * 0.12;
    // Fade at the very start and end so it never pops in or out
    const fade = Math.min(flight / 0.04, (1 - flight) / 0.04, 1);

    context.save();
    context.globalAlpha = fade;
    context.translate(x, y);
    context.rotate(tilt);

    // Glow from the engines underneath
    context.globalCompositeOperation = 'lighter';
    // Squash the drawing space so the round fade becomes an oval that reaches nothing at its edge
    context.save();
    context.translate(0, size * 0.3);
    context.scale(1, 0.42);
    const glow = context.createRadialGradient(0, 0, 0, 0, 0, size * 1.7);
    glow.addColorStop(0, 'rgb(150 255 215 / 0.32)');
    glow.addColorStop(0.35, 'rgb(120 240 200 / 0.16)');
    glow.addColorStop(0.7, 'rgb(90 210 190 / 0.05)');
    glow.addColorStop(1, 'rgb(90 210 190 / 0)');
    context.fillStyle = glow;
    context.beginPath();
    context.arc(0, 0, size * 1.7, 0, Math.PI * 2);
    context.fill();
    context.restore();
    context.globalCompositeOperation = 'source-over';

    // Glass dome
    const dome = context.createRadialGradient(-size * 0.12, -size * 0.32, size * 0.04, 0, -size * 0.15, size * 0.5);
    dome.addColorStop(0, 'rgb(220 255 240 / 0.95)');
    dome.addColorStop(0.45, 'rgb(110 230 190 / 0.85)');
    dome.addColorStop(1, 'rgb(30 120 110 / 0.8)');
    context.fillStyle = dome;
    context.beginPath();
    context.ellipse(0, -size * 0.08, size * 0.42, size * 0.38, 0, Math.PI, 0);
    context.fill();

    // Silver hull
    const hull = context.createLinearGradient(0, -size * 0.3, 0, size * 0.3);
    hull.addColorStop(0, '#f2f5f9');
    hull.addColorStop(0.45, '#aab4c2');
    hull.addColorStop(1, '#4a5361');
    context.fillStyle = hull;
    context.beginPath();
    context.ellipse(0, 0, size, size * 0.26, 0, 0, Math.PI * 2);
    context.fill();

    // Underside
    context.fillStyle = '#3a414d';
    context.beginPath();
    context.ellipse(0, size * 0.14, size * 0.5, size * 0.12, 0, 0, Math.PI * 2);
    context.fill();

    // Rim lights that blink in a chase
    const lights = 7;
    for (let k = 0; k < lights; k++) {
      const angle = Math.PI * (0.12 + (0.76 * k) / (lights - 1));
      const on = Math.sin(seconds * 6 - k * 0.9) > 0;
      context.fillStyle = on ? (k % 2 ? 'rgb(255 214 120)' : 'rgb(140 255 200)') : 'rgb(90 100 110)';
      context.beginPath();
      context.arc(Math.cos(angle) * size * 0.78, Math.sin(angle) * size * 0.14, size * 0.055, 0, Math.PI * 2);
      context.fill();
    }

    context.restore();
  }

  /**
   * A bright core, a warm glow and slowly turning rays.
   * @param {CanvasRenderingContext2D} context
   * @param {{ x: number, y: number }} at
   * @param {number} radius
   * @param {number} strength - 0 → 1.
   * @param {number} seconds
   */
  #drawSun(context, at, radius, strength, seconds) {
    const { x, y } = at;
    context.save();
    context.globalCompositeOperation = 'lighter';

    const glow = context.createRadialGradient(x, y, 0, x, y, radius * 14);
    glow.addColorStop(0, `rgb(255 244 220 / ${0.55 * strength})`);
    glow.addColorStop(0.08, `rgb(255 214 150 / ${0.3 * strength})`);
    glow.addColorStop(0.35, `rgb(255 170 90 / ${0.08 * strength})`);
    glow.addColorStop(1, 'rgb(255 150 80 / 0)');
    context.fillStyle = glow;
    context.fillRect(x - radius * 14, y - radius * 14, radius * 28, radius * 28);

    const rays = 16;
    const spin = this.#still ? 0 : seconds * 0.03;
    for (let k = 0; k < rays; k++) {
      const angle = (k / rays) * Math.PI * 2 + spin + (k % 3) * 0.07;
      const pulse = this.#still ? 1 : 0.8 + 0.2 * Math.sin(seconds * 0.6 + k * 1.7);
      const length = radius * (k % 2 ? 9 : 16) * pulse;
      const spread = k % 2 ? 0.035 : 0.022;
      const ray = context.createLinearGradient(x, y, x + Math.cos(angle) * length, y + Math.sin(angle) * length);
      ray.addColorStop(0, `rgb(255 236 200 / ${0.45 * strength})`);
      ray.addColorStop(1, 'rgb(255 220 170 / 0)');
      context.fillStyle = ray;
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x + Math.cos(angle - spread) * length, y + Math.sin(angle - spread) * length);
      context.lineTo(x + Math.cos(angle + spread) * length, y + Math.sin(angle + spread) * length);
      context.closePath();
      context.fill();
    }

    // A faint horizontal lens streak through the sun
    const streak = context.createLinearGradient(x - radius * 30, y, x + radius * 30, y);
    streak.addColorStop(0, 'rgb(255 220 180 / 0)');
    streak.addColorStop(0.5, `rgb(255 236 210 / ${0.35 * strength})`);
    streak.addColorStop(1, 'rgb(255 220 180 / 0)');
    context.fillStyle = streak;
    context.fillRect(x - radius * 30, y - radius * 0.12, radius * 60, radius * 0.24);

    if (this.#sunImage) {
      // NASA's photo of the sun's surface, with a white-hot bloom so it reads as blazing, not flat
      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = strength;
      context.save();
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.clip();
      context.drawImage(this.#sunImage, x - radius, y - radius, radius * 2, radius * 2);
      context.restore();

      context.globalCompositeOperation = 'lighter';
      const bloom = context.createRadialGradient(x, y, 0, x, y, radius * 1.4);
      bloom.addColorStop(0, `rgb(255 250 230 / ${0.35 * strength})`);
      bloom.addColorStop(0.7, `rgb(255 200 120 / ${0.25 * strength})`);
      bloom.addColorStop(1, 'rgb(255 180 90 / 0)');
      context.fillStyle = bloom;
      context.beginPath();
      context.arc(x, y, radius * 1.4, 0, Math.PI * 2);
      context.fill();
    } else {
      const core = context.createRadialGradient(x, y, 0, x, y, radius * 1.8);
      core.addColorStop(0, `rgb(255 255 255 / ${strength})`);
      core.addColorStop(0.45, `rgb(255 250 232 / ${strength})`);
      core.addColorStop(1, 'rgb(255 220 160 / 0)');
      context.fillStyle = core;
      context.beginPath();
      context.arc(x, y, radius * 1.8, 0, Math.PI * 2);
      context.fill();
    }

    context.restore();
  }

  /**
   * The moon, lit on the side facing the sun. Uses NASA's moon map once it has loaded, and a simple
   * painted moon with seas and craters until then.
   * @param {CanvasRenderingContext2D} context
   * @param {{ x: number, y: number }} at
   * @param {number} radius
   * @param {{ x: number, y: number }} sun
   * @param {number} strength - 0 → 1.
   */
  #drawMoon(context, at, radius, sun, strength) {
    const { x, y } = at;
    const [lx = -1, ly = 0] = normalize([sun.x - x, sun.y - y]);

    context.save();
    context.globalAlpha = strength;

    const halo = context.createRadialGradient(x, y, radius * 0.9, x, y, radius * 2.2);
    halo.addColorStop(0, 'rgb(210 220 240 / 0.18)');
    halo.addColorStop(1, 'rgb(210 220 240 / 0)');
    context.fillStyle = halo;
    context.beginPath();
    context.arc(x, y, radius * 2.2, 0, Math.PI * 2);
    context.fill();

    if (this.#moon) {
      // NASA's map of the moon wrapped on a sphere, lit from the sun's side, near side facing us
      this.#moon.build(Math.max(Math.round(radius * 2), 2));
      this.#moon.setLight([lx, -ly, 0.5]);
      this.#moon.draw(0);
      context.drawImage(this.#moon.canvas, x - radius, y - radius, radius * 2, radius * 2);
      context.restore();
      return;
    }

    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.clip();

    const body = context.createRadialGradient(x + lx * radius * 0.4, y + ly * radius * 0.4, 0, x, y, radius);
    body.addColorStop(0, '#efede8');
    body.addColorStop(1, '#9a9892');
    context.fillStyle = body;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);

    context.fillStyle = 'rgb(80 82 90 / 0.32)';
    for (const [dx = 0, dy = 0, size = 0] of MOON_SEAS) {
      context.beginPath();
      context.ellipse(x + dx * radius, y + dy * radius, size * radius, size * radius * 0.8, dx * 2, 0, Math.PI * 2);
      context.fill();
    }

    context.lineWidth = Math.max(radius * 0.025, 0.5);
    context.strokeStyle = 'rgb(255 255 255 / 0.35)';
    for (const [dx = 0, dy = 0, size = 0] of MOON_CRATERS) {
      context.beginPath();
      context.arc(x + dx * radius, y + dy * radius, size * radius, 0, Math.PI * 2);
      context.stroke();
    }

    // Night side: shade away from the sun with a soft edge.
    const night = context.createLinearGradient(x + lx * radius, y + ly * radius, x - lx * radius, y - ly * radius);
    night.addColorStop(0, 'rgb(4 8 20 / 0)');
    night.addColorStop(0.5, 'rgb(4 8 20 / 0.1)');
    night.addColorStop(0.75, 'rgb(4 8 20 / 0.75)');
    night.addColorStop(1, 'rgb(4 8 20 / 0.92)');
    context.fillStyle = night;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);

    context.restore();
  }

  /** Fraction of a full turn for the current moment, including the camera's swing. */
  #turn() {
    const spin = this.#still ? 0.15 : performance.now() / 1000 / TURN_SECONDS + 0.15;
    // Swinging the camera around shows another side of the planet, in the same direction it turns.
    return spin + (this.#swing * CAMERA_SWING) / (2 * Math.PI);
  }

  /** @param {number} now */
  #drawFrame(now) {
    this.#drawSky(now);
    if (!this.#planet) return;
    const swing = this.#swing;
    this.#planet.setLight(LIGHT_START.map((start, i) => start + ((LIGHT_END[i] ?? start) - start) * swing));
    this.#planet.draw(this.#turn());
  }

  #start() {
    cancelAnimationFrame(this.#loop);
    if (!this.#visible) return;
    if (this.#still) {
      this.#drawFrame(performance.now());
      return;
    }
    this.#loop = requestAnimationFrame(this.#tick);
  }

  /** @param {number} now */
  #tick = (now) => {
    this.#glide(now);
    this.#countWatching(now);
    if (now - this.#lastDraw >= FRAME_MS) {
      this.#lastDraw = now;
      this.#drawFrame(now);
    }
    this.#loop = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('name-meaning')) {
  customElements.define('name-meaning', NameMeaning);
}
